import {
	getMomentTz,
	MESApiService,
	type spParameter,
} from "@dexteel/mesf-core";
import type { DataToGetWorkOrders } from "../models/DataToGetWorkOrders";
import type { WorkOrder } from "../models/WorkOrder";

const moment = getMomentTz();

export const getWorkOrders = async (dataToGetWO: DataToGetWorkOrders) => {
	const apiService = new MESApiService();

	const parameters: spParameter[] = [];

	parameters.push({ name: "Start", value: dataToGetWO.Start });
	parameters.push({ name: "End", value: dataToGetWO.End });
	parameters.push({ name: "SearchText", value: dataToGetWO.SearchText });

	if (dataToGetWO.TypeCode !== "A")
		parameters.push({ name: "TypeCode", value: dataToGetWO.TypeCode });
	else parameters.push({ name: "TypeCode", value: null });

	if (dataToGetWO.WorkOrderStatusCode !== "A")
		parameters.push({
			name: "WorkOrderStatusCode",
			value: dataToGetWO.WorkOrderStatusCode,
		});
	else parameters.push({ name: "WorkOrderStatusCode", value: null });

	if (dataToGetWO.AssetId !== "A")
		parameters.push({
			name: "AssetId",
			value: dataToGetWO.AssetId?.toString() || null,
		});
	else parameters.push({ name: "AssetId", value: null });

	if (dataToGetWO.CraftId !== "A")
		parameters.push({ name: "CraftId", value: dataToGetWO.CraftId });
	else parameters.push({ name: "CraftId", value: null });

	const resp = await apiService.callV2("[MMS].[GetWorkOrders]", parameters);

	if (resp.ok) {
		resp.data.tables[0].rows = resp.data.tables[0].rows.map((row: any) => {
			return {
				...row,
				OpenTimestamp: row.OpenTimestamp
					? moment.utc(row.OpenTimestamp).toDate()
					: null,
				CloseTimestamp: row.CloseTimestamp
					? moment.utc(row.CloseTimestamp).toDate()
					: null,
			};
		});
	}

	return resp;
};

export const upsertWorkOrder = async (WorkOrder: WorkOrder) => {
	const apiService = new MESApiService();
	const parameters: spParameter[] = [];

	parameters.push({
		name: "WorkOrderId",
		value: WorkOrder.WorkOrderId || null,
	});
	parameters.push({ name: "AssetId", value: WorkOrder.AssetId });
	parameters.push({ name: "Title", value: WorkOrder.Title || "" });
	parameters.push({ name: "DownHours", value: WorkOrder.DownHours || null });
	parameters.push({ name: "CraftId", value: WorkOrder.CraftId || null });
	parameters.push({ name: "Comments", value: WorkOrder.Comments || null });
	parameters.push({
		name: "WorkOrderNumberOfWorkers",
		value: WorkOrder.WorkOrderNumberOfWorkers || null,
	});
	parameters.push({
		name: "OpenTimestamp",
		value: moment(WorkOrder.OpenTimestamp, "MM/DD/YYYY HH:mm")
			.utc()
			.toISOString(),
	});
	parameters.push({
		name: "WorkOrderPlannedHours",
		value: WorkOrder.WorkOrderPlannedHours || null,
	});
	const resp = await apiService.callV2("[MMS].[UpsertWorkOrder]", parameters);
	return resp;
};

export const deleteWorkOrder = async (wO: WorkOrder) => {
	const apiService = new MESApiService();

	const parameters: spParameter[] = [];

	parameters.push({ name: "WorkOrderId", value: wO.WorkOrderId });

	const resp = await apiService.callV2("[MMS].[DeleteWorkOrder]", parameters);
	return resp;
};
export const deleteWorkOrders = async ({
	workOrdersCSV,
	comments,
}: { workOrdersCSV: string; comments: string }) => {
	const apiService = new MESApiService();

	const parameters: spParameter[] = [];

	parameters.push({ name: "WorkOrders", value: workOrdersCSV });
	parameters.push({ name: "Comments", value: comments });

	const resp = await apiService.callV2("[MMS].[DeleteWorkOrders]", parameters);
	return resp;
};

export const closeWorkOrders = async (
	WorkOrderId: number,
	Comments: string,
) => {
	const apiService = new MESApiService();

	const parameters: spParameter[] = [];

	parameters.push({ name: "WorkOrderId", value: WorkOrderId });
	parameters.push({ name: "Comments", value: Comments });

	const resp = await apiService.callV2("[MMS].[CloseWorkOrder]", parameters);
	return resp;
};

export const deleteWorkOrderById = async (WorkOrderId: number | null) => {
	const apiService = new MESApiService();

	const parameters: spParameter[] = [];

	parameters.push({ name: "WorkOrderId", value: WorkOrderId });

	const resp = await apiService.callV2("[MMS].[DeleteWorkOrder]", parameters);
	return resp;
};

export const closeWorkOrder = async (wO: WorkOrder) => {
	const apiService = new MESApiService();

	const parameters: spParameter[] = [];

	parameters.push({ name: "WorkOrderId", value: wO.WorkOrderId });
	parameters.push({ name: "Comments", value: wO.Comments });
	parameters.push({ name: "AssetId", value: wO.AssetId });
	parameters.push({ name: "Title", value: wO.Title });
	parameters.push({ name: "DownHours", value: wO.DownHours });
	parameters.push({ name: "CraftId", value: wO.CraftId });
	parameters.push({
		name: "CloseTimestamp",
		value: moment.utc(wO.CloseTimestamp).toISOString(),
	});
	parameters.push({ name: "ConsumedHours", value: wO.ConsumedHours });
	parameters.push({
		name: "NumberOfWorkers",
		value: wO.WorkOrderNumberOfWorkers,
	});

	const resp = await apiService.callV2("[MMS].[CloseWorkOrder]", parameters);
	return resp;
};

export const openWorkOrder = async (wO: WorkOrder) => {
	const apiService = new MESApiService();

	const parameters: spParameter[] = [];

	parameters.push({ name: "WorkOrderId", value: wO.WorkOrderId });

	const resp = await apiService.callV2("[MMS].[openWorkOrder]", parameters);
	return resp;
};

//WORK ORDERS ATTACHMENT

export const getWorkOrderAttachments = async (
	WorkOrderId: string | number | undefined,
) => {
	const apiService = new MESApiService();

	const parameters: spParameter[] = [];

	parameters.push({
		name: "WorkOrderId",
		value: Number.parseInt(WorkOrderId as string),
	});

	const resp = await apiService.callV2(
		"[MMS].[GetWorkOrderAttachments]",
		parameters,
	);
	return resp;
};

export const linkWorkOrderAttachments = async (
	workOrderId: number,
	attachments: {
		UploadFileId: number;
		FileName: string;
		IsAssetResource: boolean;
	}[],
) => {
	const apiService = new MESApiService();
	const formattedAttachments = attachments
		.map(
			(att) =>
				`[${att.UploadFileId}]:[${att.FileName}]:${
					att.IsAssetResource ? 1 : 0
				}`,
		)
		.join(",");

	const parameters: spParameter[] = [
		{ name: "WorkOrderId", value: workOrderId },
		{ name: "Attachments", value: formattedAttachments },
	];

	const resp = await apiService.callV2(
		"[MMS].[LinkWorkOrderAttachments]",
		parameters,
	);
	return resp;
};

export const unLinkWorkOrderAttachments = async (
	workOrderId: number,
	attachments: { UploadFileId: number; IsAssetResource: boolean }[],
) => {
	const apiService = new MESApiService();
	const formattedAttachments = attachments
		.map((att) => `[${att.UploadFileId}]:${att.IsAssetResource ? 1 : 0}`)
		.join(",");

	const parameters: spParameter[] = [
		{ name: "WorkOrderId", value: workOrderId },
		{ name: "Attachments", value: formattedAttachments },
	];

	const resp = await apiService.callV2(
		"[MMS].[UnlinkWorkOrderAttachments]",
		parameters,
	);
	return resp;
};

// ===============================================================================
// DRAWINGS
// ===============================================================================

export const getWorkOrderDrawings = async (WorkOrderId: number | string) => {
	const apiService = new MESApiService();

	const parameters: spParameter[] = [];

	parameters.push({
		name: "WorkOrderId",
		value: Number.parseInt(WorkOrderId as string),
	});

	const resp = await apiService.callV2(
		"[MMS].[GetWorkOrderDrawings]",
		parameters,
	);
	return resp;
};

export const linkWorkOrderDrawings = async (
	workOrderId: number,
	documentNames: { DocumentName: string; IsAssetResource: boolean }[],
) => {
	const apiService = new MESApiService();
	const formattedDocumentNames = documentNames
		.map((doc) => `[${doc.DocumentName}]:${doc.IsAssetResource ? 1 : 0}`)
		.join(",");

	const parameters: spParameter[] = [
		{ name: "WorkOrderId", value: workOrderId },
		{ name: "DocumentNames", value: formattedDocumentNames },
	];

	const resp = await apiService.callV2(
		"[MMS].[LinkWorkOrderDrawings]",
		parameters,
	);
	return resp;
};

export const unLinkWorkOrderDrawings = async (
	workOrderId: number,
	documentNames: { DocumentName: string; IsAssetResource: boolean }[],
) => {
	const apiService = new MESApiService();
	const formattedDocumentNames = documentNames
		.map((doc) => `[${doc.DocumentName}]:${doc.IsAssetResource ? 1 : 0}`)
		.join(",");

	const parameters: spParameter[] = [
		{ name: "WorkOrderId", value: workOrderId },
		{ name: "DocumentNames", value: formattedDocumentNames },
	];

	const resp = await apiService.callV2(
		"[MMS].[UnlinkWorkOrderDrawings]",
		parameters,
	);
	return resp;
};

// ===============================================================================
// LOTO
// ===============================================================================

export const getWorkOrderLOTO = async (WorkOrderId: number | string) => {
	const apiService = new MESApiService();

	const parameters: spParameter[] = [];

	parameters.push({
		name: "WorkOrderId",
		value: Number.parseInt(WorkOrderId as string),
	});

	const resp = await apiService.callV2("[MMS].[GetWorkOrderLOTO]", parameters);
	return resp;
};

export const linkWorkOrderLOTO = async (
	WorkOrderId: number | string,
	LOTOs: { LOTONumber: string; LOTOName: string; IsAssetResource: boolean }[],
) => {
	const apiService = new MESApiService();
	const formattedLOTOs = LOTOs.map(
		(LOTO) =>
			`[${LOTO.LOTONumber}]:[${LOTO.LOTOName}]:${LOTO.IsAssetResource ? 1 : 0}`,
	).join(",");

	const parameters: spParameter[] = [
		{ name: "WorkOrderId", value: WorkOrderId },
		{ name: "LOTOs", value: formattedLOTOs },
	];

	const resp = await apiService.callV2("[MMS].[LinkWorkOrderLOTO]", parameters);
	return resp;
};

export const unLinkWorkOrderLOTO = async (
	workOrderId: number,
	LOTOs: { LOTONumber: string; LOTOName: string; IsAssetResource: boolean }[],
) => {
	const apiService = new MESApiService();
	const formattedLOTOs = LOTOs.map(
		(LOTO) =>
			`[${LOTO.LOTONumber}]:[${LOTO.LOTOName}]:${LOTO.IsAssetResource ? 1 : 0}`,
	).join(",");

	const parameters: spParameter[] = [
		{ name: "WorkOrderId", value: workOrderId },
		{ name: "LOTOs", value: formattedLOTOs },
	];

	const resp = await apiService.callV2(
		"[MMS].[UnlinkWorkOrderLOTO]",
		parameters,
	);
	return resp;
};

// ===============================================================================
// PARTS
// ===============================================================================

export const getWorkOrderParts = async (WorkOrderId: number | string) => {
	const apiService = new MESApiService();

	const parameters: spParameter[] = [];

	parameters.push({
		name: "WorkOrderId",
		value: Number.parseInt(WorkOrderId as string),
	});

	const resp = await apiService.callV2("[MMS].[GetWorkOrderParts]", parameters);
	return resp;
};

export const linkWorkOrderParts = async (
	WorkOrderId: number | string,
	Parts: { PartName: string; IsAssetResource: boolean }[],
) => {
	const apiService = new MESApiService();

	const formattedParts = Parts.map(
		(part) => `[${part.PartName}]:[${part.IsAssetResource ? 1 : 0}]`,
	).join(",");

	const parameters: spParameter[] = [
		{ name: "WorkOrderId", value: Number.parseInt(WorkOrderId as string) },
		{ name: "Parts", value: formattedParts },
	];

	const resp = await apiService.callV2(
		"[MMS].[LinkWorkOrderParts]",
		parameters,
	);
	return resp;
};

export const unLinkWorkOrderParts = async (
	WorkOrderId: number,
	parts: { PartName: string; IsAssetResource: boolean }[],
) => {
	const apiService = new MESApiService();
	const formattedParts = parts
		.map((part) => `[${part.PartName}]:${part.IsAssetResource ? 1 : 0}`)
		.join(",");

	const parameters: spParameter[] = [
		{ name: "WorkOrderId", value: WorkOrderId },
		{ name: "Parts", value: formattedParts },
	];

	const resp = await apiService.callV2(
		"[MMS].[UnlinkWorkOrderParts]",
		parameters,
	);
	return resp;
};
// ===============================================================================

// Get functions

export const getWorkOrderResources = async (WorkOrderId: number | string) => {
	const apiService = new MESApiService();

	const parameters: spParameter[] = [];

	parameters.push({ name: "WorkOrderId", value: WorkOrderId });

	const resp = await apiService.callV2(
		"[MMS].[GetWorkOrderResources]",
		parameters,
	);
	return resp;
};
